import React from "react";
import { Skeleton } from "antd";
import Swal from "sweetalert2";
import APIService from "../../Service/APIService";
import PermissionEditor from "../UserPermissions/PermissionEditor";
import Button from "../../common/button/button";
import Text from "../../common/typography/text.js";
import { useAuth } from "../AuthProvider.js";
import { canAdminPerformUserAction } from "./userActionAccess.js";
import "./UserDetails.css";

/**
 * Per-user permissions panel for the User page's "Permissions" tab.
 *
 * Reuses the existing permission APIs and the presentational PermissionEditor:
 *   GET getpermissiondefinitions        -> registry (defs)
 *   GET getuserpermissions?UserID=       -> resolved permissions for this user
 *   PUT updateuserpermissions            -> persist changes
 *
 * Self-contained edit/save (its own Edit/Save buttons) so it doesn't entangle
 * with the Details tab's form state.
 */
const UserPermissionsTab = ({ userId, userType }) => {
  const { user } = useAuth();
  const apiRef = React.useRef(new APIService());

  const [registry, setRegistry] = React.useState([]);
  const [resolved, setResolved] = React.useState({});
  const [pending, setPending] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [defsRes, permRes] = await Promise.all([
          apiRef.current.getDataFromBackend("getpermissiondefinitions"),
          apiRef.current.getDataFromBackend(
            `getuserpermissions?UserID=${userId}`
          ),
        ]);
        if (cancelled) return;
        const definitions = defsRes?.data?.data || {};
        setRegistry(
          Array.isArray(definitions)
            ? definitions
            : Object.entries(definitions).map(([key, definition]) => ({
                key,
                ...definition,
              })),
        );
        setResolved(permRes?.data?.data?.Permissions || {});
      } catch (error) {
        if (!cancelled) {
          setRegistry([]);
          setResolved({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (userId) load();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const filteredDefs = React.useMemo(
    () => registry.filter((def) => def.appliesTo?.includes(userType)),
    [registry, userType]
  );

  const effectiveValues = React.useMemo(
    () => ({ ...resolved, ...pending }),
    [resolved, pending]
  );

  const hasPending = Object.keys(pending).length > 0;
  const canEditPermissions = canAdminPerformUserAction(
    user,
    "user.permissions.edit",
  ) && Boolean(user?.IsSuperAdmin);
  const isSelf = user?.ID && String(user.ID) === String(userId);

  const handleToggle = (key, checked) => {
    if (!canEditPermissions || !editing || isSelf) return;
    setPending((prev) => ({ ...prev, [key]: checked }));
  };

  const handleSave = async () => {
    if (!canEditPermissions || isSelf) return;
    if (!hasPending) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const changes = Object.entries(pending).map(
        ([PermissionKey, IsGranted]) => ({ PermissionKey, IsGranted })
      );
      const response = await apiRef.current.updateDataToBackend(
        { UserID: userId, Changes: changes },
        "updateuserpermissions"
      );
      setResolved(response?.data?.data?.Permissions || effectiveValues);
      setPending({});
      setEditing(false);
      Swal.fire("Saved", "Permissions updated successfully.", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to save permissions",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  if (filteredDefs.length === 0) {
    return (
      <Text variant="tinyText" color="lightText">
        No permissions are configurable for this user type.
      </Text>
    );
  }

  return (
    <div className="user-permissions-tab">
      {canEditPermissions && !isSelf && (
        <div className="user-permissions-tab-actions">
          {editing ? (
            <Button
              variant="table"
              subVariant="success"
              style={{ minWidth: "100px" }}
              disabled={saving}
              onClick={handleSave}
            >
              Save
            </Button>
          ) : (
            <Button
              variant="table"
              subVariant="warning"
              style={{ minWidth: "100px" }}
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
          )}
        </div>
      )}
      <PermissionEditor
        defs={filteredDefs}
        values={effectiveValues}
        onToggle={handleToggle}
        disabled={!canEditPermissions || !editing || isSelf}
      />
    </div>
  );
};

export default UserPermissionsTab;
